<template>
  <BaseModal :modalTitle="$t('W: Ny analyse')">
    <ModalStepGuide
      :stepTitles="[$t('W: Generelt'), $t('area.analysis.tab.analysis-tests')]"
      :currentStep="currentStep" />

    <!-- Step 1: Basic info -->
    <div v-if="currentStep === 1">
      <UiBaseFormVee
        :errorText="errorText"
        :onSubmit="onStep1Next">
        <UiInputTextFieldVee
          name="name"
          :label="$t('term.name')"
          :placeholder="$t('form.placeholder.name')"
          :unit="currentLocale.toUpperCase()"
          :rules="['required']"
          isRequired
          showAsRequired
          v-model="form.name" />
        <UiInputSelectVee
          name="minimumTurnaroundTimeID"
          :label="$t('area.analysis.field.min-tat')"
          :options="minimumTatOptions"
          v-model="form.minimumTurnaroundTimeID" />
        <UiInputSelectVee
          name="maximumTurnaroundTimeID"
          :label="$t('area.analysis.field.max-tat')"
          :options="maximumTatOptions"
          v-model="form.maximumTurnaroundTimeID" />

        <UiInputSelectVee
          name="labReportTemplateID"
          :label="$t('W: Rapportskabelon')"
          :options="reportTemplateOptions"
          :rules="['required']"
          isRequired
          v-model="form.labReportTemplateID" />
        <UiInputCheckboxGroupVee
          name="sampleAreaIDs"
          :label="$t('W: Prøveområder')"
          :options="sampleAreaOptions"
          :rules="['required']"
          isRequired
          v-model="form.sampleAreaIDs" />
        <div class="mt-4">
          <span class="text-sm font-medium text-text-primary">{{ $t('W: Indstillinger') }}</span>
          <div class="mt-2 mb-4 flex items-center gap-2">
            <AppSwitch
              :isActive="form.isOnlyOnePerSample"
              :title="$t('W: Kun én analyse pr. prøve')"
              @on-toggle="form.isOnlyOnePerSample = !form.isOnlyOnePerSample" />
            <span>{{ $t('W: Kun én analyse pr. prøve') }}</span>
          </div>
          <div class="mb-4 flex items-center gap-2">
            <AppSwitch
              :isActive="form.equipmentSelectionEnabled"
              :title="$t('W: Udstyrsvælger ved resultatindtastning')"
              @on-toggle="form.equipmentSelectionEnabled = !form.equipmentSelectionEnabled" />
            <span>{{ $t('W: Udstyrsvælger ved resultatindtastning') }}</span>
          </div>
        </div>
        <template #buttons>
          <UiBaseButton
            :text="$t('action.next')"
            type="submit" />
        </template>
      </UiBaseFormVee>
    </div>

    <!-- Step 2: Analysis tests -->
    <div v-else-if="currentStep === 2">
      <p class="mb-2 block text-sm font-medium text-text-primary">
        {{ $t('W: Valgte analysetests') }}
      </p>

      <UiEditableList
        class="mb-4"
        :items="combinedTestItems"
        isDraggable
        isRemovable
        :emptyStateText="$t('W: Ingen analysetests tilføjet endnu')"
        :addOptions="testAddOptions"
        :addPlaceholder="$t('term.search')"
        :createNewLabel="$t('W: Opret ny test')"
        @update:items="onTestItemsReorder"
        @on-remove="onTestItemRemove"
        @on-add="onTestAdd"
        @on-create-new="onAddNewTestClick">
        <template #item="{ item }">
          <div class="flex min-w-0 flex-1 flex-col">
            <span class="truncate text-sm text-text-primary">
              {{ typeof item === 'string' ? getAnalysisTestName(item) : asStagedTest(item).name }}
            </span>
            <span class="truncate text-xs text-text-secondary">
              {{
                typeof item === 'string'
                  ? getAnalysisTestTypeName(item)
                  : getStagedTestTypeName(asStagedTest(item).type)
              }}
            </span>
          </div>
        </template>
      </UiEditableList>
    </div>

    <template
      v-if="currentStep > 1"
      #buttons>
      <UiBaseButton
        :text="$t('action.back')"
        :isDisabled="isLoading"
        @button-click="prevStep" />
      <UiBaseButton
        v-if="currentStep === 2"
        :text="$t('action.create')"
        :isLoading="isLoading"
        @button-click="onCreate" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useModalStore } from '@/stores/modalStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import { buildAnalysisTestCreatePayload } from '@/globals/typescript/utils/analysisTestFocusForm'
import type { TAnalysisTestFocusForm } from '@/globals/typescript/utils/analysisTestFocusForm'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreateAnalysisModal',

  props: {
    // Pre-checks this Sample Area in the checkbox group when opened from
    // within its own content view — still editable, not locked.
    initialSampleAreaID: {
      type: String,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      currentStep: 1 as number,
      isLoading: false,
      errorText: '',
      form: {
        name: '' as string,
        minimumTurnaroundTimeID: '' as string,
        maximumTurnaroundTimeID: '' as string,
        analysisTestIDs: [] as string[],
        sampleAreaIDs: (this.initialSampleAreaID ? [this.initialSampleAreaID] : []) as string[],
        isOnlyOnePerSample: true as boolean,
        equipmentSelectionEnabled: false as boolean,
        labReportTemplateID: '' as string,
        newAnalysisTests: [] as TAnalysisTestFocusForm[],
      },
    }
  },

  computed: {
    ...mapState(useAnalysisTestStore, ['allAnalysisTests']),
    ...mapState(useSampleAreaStore, ['allSampleAreas']),
    ...mapState(useReportTemplateStore, ['allReportTemplates']),
    ...mapState(useTurnaroundTimeStore, ['allTATs']),
    ...mapState(useAppStore, ['currentLocale']),

    minimumTatOptions(): { value: string; text: string }[] {
      const selectedMaximumTat = this.allTATs.find(
        (x) => x.id === this.form.maximumTurnaroundTimeID
      )
      const noneOption = { value: '', text: this.$t('term.none') }
      const tats = this.allTATs
        .filter((tat) =>
          tat.isCompatibleWithMinMax({ maximumTat: selectedMaximumTat })
        )
        .map((tat) => ({
          value: tat.id,
          text: tat.getName(this.currentLocale),
        }))
      return [noneOption, ...tats]
    },

    maximumTatOptions(): { value: string; text: string }[] {
      const selectedMinimumTat = this.allTATs.find(
        (x) => x.id === this.form.minimumTurnaroundTimeID
      )
      const noneOption = { value: '', text: this.$t('term.none') }
      const tats = this.allTATs
        .filter((tat) =>
          tat.isCompatibleWithMinMax({ minimumTat: selectedMinimumTat })
        )
        .map((tat) => ({
          value: tat.id,
          text: tat.getName(this.currentLocale),
        }))
      return [noneOption, ...tats]
    },

    analysisTestOptions(): { value: string; text: string }[] {
      return this.allAnalysisTests.map((t) => ({
        value: t.id,
        text: t.getName(this.currentLocale),
      }))
    },

    testAddOptions(): { value: string; text: string }[] {
      return this.analysisTestOptions.filter(
        (option) => !this.form.analysisTestIDs.includes(option.value)
      )
    },

    combinedTestItems(): (string | TAnalysisTestFocusForm)[] {
      return [...this.form.analysisTestIDs, ...this.form.newAnalysisTests]
    },

    reportTemplateOptions(): { value: string; text: string }[] {
      return this.allReportTemplates
        .filter(
          (x) =>
            x.type === 'lab' &&
            (x.activeState === 'active' || x.id === this.form.labReportTemplateID)
        )
        .map((template) => ({
          value: template.id,
          text: template.getTitle(this.currentLocale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    sampleAreaOptions(): { value: string; text: string }[] {
      return [...this.allSampleAreas]
        .filter((x) => x.activeState === 'active')
        .sort((a, b) =>
          a
            .getName(this.currentLocale)
            .localeCompare(b.getName(this.currentLocale))
        )
        .map((sampleArea) => ({
          value: sampleArea.id,
          text: sampleArea.getName(this.currentLocale),
        }))
    },
  },

  watch: {
    'form.sampleAreaIDs'() {
      this.autoFillReportTemplateID()
    },
  },

  methods: {
    ...mapActions(useAnalysisStore, ['createAnalysis']),
    ...mapActions(useModalStore, ['open', 'closeTop']),

    // Only ever fills an empty field — never overwrites a report template
    // the user already picked (or one already auto-filled). Uses the first
    // selected sample area's own default, matching how the create modal
    // pre-selects a sample area today.
    autoFillReportTemplateID() {
      if (this.form.labReportTemplateID) return
      const firstSampleAreaID = this.form.sampleAreaIDs[0]
      if (!firstSampleAreaID) return
      const sampleArea = this.allSampleAreas.find((x) => x.id === firstSampleAreaID)
      if (sampleArea?.labReportTemplateID) {
        this.form.labReportTemplateID = sampleArea.labReportTemplateID
      }
    },

    async onStep1Next(): Promise<void> {
      this.errorText = ''
      this.currentStep = 2
    },

    prevStep() {
      this.currentStep -= 1
    },

    asStagedTest(item: unknown): TAnalysisTestFocusForm {
      return item as TAnalysisTestFocusForm
    },

    onTestItemsReorder(items: unknown[]) {
      const ids: string[] = []
      const staged: TAnalysisTestFocusForm[] = []
      for (const item of items) {
        if (typeof item === 'string') ids.push(item)
        else staged.push(item as TAnalysisTestFocusForm)
      }
      this.form.analysisTestIDs = ids
      this.form.newAnalysisTests = staged
    },

    onTestItemRemove(item: unknown) {
      if (typeof item === 'string') this.removeAnalysisTest(item)
      else this.removeStagedNewTest(this.asStagedTest(item).id)
    },

    onTestAdd(value: string) {
      this.form.analysisTestIDs.push(value)
    },

    removeAnalysisTest(id: string) {
      const idx = this.form.analysisTestIDs.indexOf(id)
      if (idx !== -1) this.form.analysisTestIDs.splice(idx, 1)
    },

    getAnalysisTestName(id: string): string {
      const test = this.allAnalysisTests.find((t) => t.id === id)
      return test ? test.getName(this.currentLocale) : id
    },

    getAnalysisTestTypeName(id: string): string {
      const test = this.allAnalysisTests.find((t) => t.id === id)
      return test?.typeName ?? ''
    },

    getStagedTestTypeName(type: TAnalysisTestFocusForm['type']): string {
      if (!type) return ''
      return this.$t(`area.analysis-test.type.${type}`)
    },

    onAddNewTestClick() {
      this.open({
        name: 'CreateAnalysisTestModal',
        width: 'md',
        props: {
          onStagedComplete: (form: TAnalysisTestFocusForm) => {
            this.form.newAnalysisTests.push({ ...form, id: generateFirestoreId() })
          },
        },
      })
    },

    removeStagedNewTest(id: string) {
      const idx = this.form.newAnalysisTests.findIndex((t) => t.id === id)
      if (idx !== -1) this.form.newAnalysisTests.splice(idx, 1)
    },

    async onCreate(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.createAnalysis({
          name: this.form.name,
          minimumTurnaroundTimeID: this.form.minimumTurnaroundTimeID || null,
          maximumTurnaroundTimeID: this.form.maximumTurnaroundTimeID || null,
          analysisTestIDs: this.form.analysisTestIDs,
          sampleAreaIDs: this.form.sampleAreaIDs,
          isOnlyOnePerSample: this.form.isOnlyOnePerSample,
          equipmentSelectionEnabled: this.form.equipmentSelectionEnabled,
          labReportTemplateID: this.form.labReportTemplateID,
          newAnalysisTests: this.form.newAnalysisTests.map((staged) => ({
            ...buildAnalysisTestCreatePayload(staged),
            id: staged.id,
          })),
        })
        useToast().success(this.$t('W: Analyse oprettet'))
        this.closeTop()
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette analyse'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.autoFillReportTemplateID()
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

